import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { Sparkles, Upload, CheckCircle2, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { openRazorpay } from "@/lib/razorpay";

const OCCASIONS = ["जन्मदिन", "वर्षगाँठ", "बधाई", "पदोन्नति", "शादी की सालगिरह", "पुण्य तिथि / श्रद्धांजलि", "अन्य"];

export default function SubmitWishPage() {
  const [packages, setPackages] = useState({ basic: 51, featured: 101, premium: 501 });
  const [pkg, setPkg] = useState("basic");
  const [form, setForm] = useState({ occasion: "जन्मदिन", recipient_name: "", from_name: "", message: "", photo_url: "", contact_phone: "" });
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const photoRef = useRef();

  useEffect(() => { api.get("/wishes/packages").then((r) => setPackages(r.data)).catch(() => {}); }, []);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload?watermark=false", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_url: data.url }));
      toast.success("फ़ोटो अपलोड हो गयी");
    } catch (e) { toast.error(formatError(e)); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.recipient_name.trim() || !form.from_name.trim() || !form.contact_phone.trim()) {
      toast.error("जिसे शुभकामना दे रहे हैं, आपका नाम और फ़ोन सब भरें");
      return;
    }
    setProcessing(true);
    try {
      const { data } = await api.post("/wishes/submit-paid", { ...form, package_id: pkg });
      const res = await openRazorpay({
        order_id: data.order_id, amount: data.amount, currency: data.currency, key_id: data.key_id,
        description: `Wish - ${form.occasion}`,
        prefill: { name: form.from_name, contact: form.contact_phone },
      });
      if (res.ok) {
        // Verify with wishes-specific verify (since generic verify already ran, backend may have handled it via kind check)
        try {
          await api.post("/wishes/verify", {
            razorpay_order_id: data.order_id,
            razorpay_payment_id: res.data?.razorpay_payment_id || "",
            razorpay_signature: res.data?.razorpay_signature || "",
          });
        } catch {}
        setSuccess(true);
      }
    } catch (e) { toast.error(formatError(e)); }
    finally { setProcessing(false); }
  };

  if (success) {
    return (
      <>
        <PublicHeader/>
        <main className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="wish-success">
          <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-amber-600"/>
          </div>
          <h1 className="font-devnagri-heading text-4xl font-black text-[#0F172A]">भुगतान सफल! 🎉</h1>
          <p className="text-slate-600 mt-4">आपकी शुभकामना अगले 7 दिनों तक e-Paper में प्रकाशित होगी।</p>
          <a href="/" className="inline-block mt-6 bg-[#0F172A] text-white px-6 py-2 rounded font-bold">होम पर जाएँ</a>
        </main>
        <PublicFooter/>
      </>
    );
  }

  return (
    <>
      <PublicHeader/>
      <section className="bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 text-white">
        <div className="max-w-4xl mx-auto px-4 py-14">
          <div className="flex items-center gap-3 mb-3">
            <Sparkles className="w-6 h-6"/>
            <span className="uppercase tracking-[0.28em] text-[11px] font-black">Paid Wishes · e-Paper में शुभकामना</span>
          </div>
          <h1 className="font-devnagri-heading text-4xl md:text-5xl font-black">अपने अपनों की <span className="text-yellow-100">खुशियाँ अख़बार में</span></h1>
          <p className="text-orange-50 mt-3 max-w-2xl">जन्मदिन, बधाई, वर्षगाँठ या श्रद्धांजलि — INDIAN-NEWS-21 के e-Paper में 7 दिन तक प्रकाशित करें।</p>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-4 py-10 grid grid-cols-1 lg:grid-cols-5 gap-8" data-testid="submit-wish-page">
        <aside className="lg:col-span-2">
          <h3 className="font-bold text-[#0F172A] mb-3">पैकेज चुनें</h3>
          <div className="space-y-2">
            {Object.entries(packages).map(([id, price]) => (
              <button key={id} onClick={() => setPkg(id)}
                className={`w-full text-left p-4 rounded-lg border-2 transition ${pkg === id ? "border-amber-500 bg-amber-50" : "border-slate-200 bg-white"}`}
                data-testid={`wpkg-${id}`}>
                <div className="flex justify-between items-baseline">
                  <div className="font-bold text-[#0F172A] capitalize">{id === "basic" ? "बेसिक" : id === "featured" ? "फ़ीचर्ड" : "प्रीमियम"}</div>
                  <div className="text-2xl font-black text-emerald-700">₹{price}</div>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {id === "basic" ? "टेक्स्ट शुभकामना · 7 दिन" : id === "featured" ? "फ़ोटो सहित · 7 दिन · हाईलाइट" : "फ़ोटो + बड़े साइज़ · 7 दिन · टॉप पर"}
                </div>
              </button>
            ))}
          </div>
        </aside>
        <div className="lg:col-span-3 bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
          <h3 className="font-bold text-lg text-[#0F172A] mb-4">शुभकामना का विवरण</h3>
          <div className="space-y-3">
            <select value={form.occasion} onChange={(e) => setForm({ ...form, occasion: e.target.value })} className="w-full h-10 px-3 rounded-md border border-slate-200" data-testid="occ">
              {OCCASIONS.map((o) => <option key={o}>{o}</option>)}
            </select>
            <Input placeholder="जिसे शुभकामना देनी है (नाम) *" value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} data-testid="rec"/>
            <Input placeholder="आपका नाम *" value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} data-testid="frm"/>
            <Input placeholder="आपका फ़ोन *" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="ph"/>
            <Textarea rows={3} maxLength={300} placeholder="संदेश (वैकल्पिक)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="msg"/>
            <div className="flex items-center gap-3">
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-md border border-dashed border-slate-300 hover:border-amber-500 text-sm">
                <Upload className="w-4 h-4"/>{uploading ? "…" : "फ़ोटो अपलोड (वैकल्पिक)"}
                <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}/>
              </label>
              {form.photo_url && <img src={form.photo_url} alt="" className="w-14 h-14 rounded-full object-cover"/>}
            </div>
            <Button onClick={submit} disabled={processing} className="w-full mt-3 bg-gradient-to-r from-amber-500 to-red-500 hover:from-amber-600 hover:to-red-600 text-white py-6 text-lg font-bold" data-testid="wish-pay">
              <Heart className="w-5 h-5 mr-2 fill-white"/>{processing ? "प्रक्रिया…" : `₹${packages[pkg]} भुगतान करें`}
            </Button>
          </div>
        </div>
      </main>
      <PublicFooter/>
    </>
  );
}
